/**
 *
 * Select
 *
 */

import React from "react";
import {
  Label,
  Input,
  Wrapper,
  Option,
  Options,
  SelectIcon,
} from "./styledComponents";

import InputValidator from "../InputValidator";
import Icon from "../Icon";
import DraggableList from '../DraggableList';
import Tooltip from "components/Tooltip";
import { colors } from "globalStyles";

// Monotonic fallback for id generation — class components cannot useId(), and
// the combobox ARIA wiring (label htmlFor, aria-controls, aria-activedescendant)
// needs ids that stay unique when several Selects share a page.
let selectIdSeq = 0;

class Select extends React.PureComponent {
  // Local initial state on mount
  state = {
    isOpen: false,
    inputValue: "",
    fromOptions: true,
    shouldReload: false,
    // Index into navigableOptions() of the option targeted by keyboard
    // navigation (aria-activedescendant); -1 = none active.
    activeIndex: -1,
  };

  baseId =
    this.props.id || `select-${this.props.name || "field"}-${++selectIdSeq}`;

  // Lifecycle hooks
  componentDidMount() {
    document.addEventListener("click", this.onOutsideClick);
    document.addEventListener("keydown", this.onKeyDown);
    // Seed the visible input text from the current value. This is a
    // SINGLE-select concern: there is one value and one label to show for it.
    //
    // A multi-select must NOT come through here. `handleOptionSelected` means
    // "the user picked ONE option" and appends its argument to the selection —
    // so calling it at mount with the WHOLE current selection appends the list
    // to itself. When the selection is empty, ImageSelect.getValues() returns
    // `input.value` unchanged (its mapping branch is gated on `value[0]`), so
    // `this.props.value` and `field.value` are the SAME array object and the
    // append becomes `list.push(list)` — a self-referential array in the form
    // state. react-hook-form clones the whole form value on every change with
    // an unguarded recursive `cloneObject`, so the next change blew the stack:
    // MOVEBOOTH-REACT-B (`RangeError: Maximum call stack size exceeded`, thrown
    // as an unhandled rejection out of RHF's async onChange). ImageSelect's own
    // componentDidMount then replaced the value, which is why the page kept
    // working and the fault stayed invisible for six days.
    if (this.props.value && !this.props.isMultiSelect) {
      this.handleOptionSelected(this.props.value);
    }
  }

  componentWillUnmount() {
    document.removeEventListener("click", this.onOutsideClick);
    document.removeEventListener("keydown", this.onKeyDown);
  }

  componentDidUpdate(prevProps, prevState) {
    // Reconcile the keyboard-active row by IDENTITY when an async fetch
    // (paged fetchOptions, search fetch) replaces the options while a row is
    // highlighted: the highlighted VALUE stays active at its new position,
    // and a vanished value deactivates — so Enter can never commit whatever
    // option shifted into the stale positional index. (Typing resets
    // activeIndex to -1, so a surviving activeIndex was set against the
    // current inputValue and the prev/next lists are comparable.)
    if (prevProps.options !== this.props.options && this.state.activeIndex >= 0) {
      const prevItem = this.navigableOptionsOf(prevProps.options)[
        this.state.activeIndex
      ];
      const activeIndex = !prevItem
        ? -1
        : prevItem.isNewOption
        ? 0
        : this.navigableOptions().findIndex(
            (o) => !o.isNewOption && o.value === prevItem.value
          );
      if (activeIndex !== this.state.activeIndex) {
        this.setState({ activeIndex });
      }
    }

    if (prevProps.value !== this.props.value && !this.props.isMultiSelect) {
      var newInputVal = this.props.value;
      const option = (this.props.options || []).find(
        (x) => x.value === newInputVal
      );
      this.updateInputValue(option ? option.label : newInputVal);
    }

    if (!prevState.isOpen && this.state.isOpen) {
      const list = document.getElementsByClassName("options-list");
      if (list && list.length > 0) {
        list[0].addEventListener("scroll", this.handleFetchOptionsScroll);
      }
    }

    if (prevProps.value && !this.props.value) {
      this.setState({ shouldReload: true });
    }
    if (this.state.shouldReload) {
      this.setState({ shouldReload: false });
    }
  }

  componentWillUpdate(_nextProps, _nextState) {
    if (!_nextState.isOpen && this.state.isOpen) {
      const list = document.getElementsByClassName("options-list");
      if (list && list.length > 0) {
        list[0].removeEventListener("scroll", this.handleFetchOptionsScroll);
      }
    }
  }

  /**
   *
   * Recursively check for parent node containing the ref node
   * return true if the target or parent nodes do not match
   * the refArea
   *
   * @param  {object|undefined} target A node or parent node of passed in target
   *
   * @return {boolean}
   */
  clickWasOutside = (target) =>
    target === this.refArea
      ? false
      : !!target
      ? this.clickWasOutside(target.parentNode)
      : true;

  onOutsideClick = (e) => {
    e.stopPropagation();

    if (this.state.isOpen && this.clickWasOutside(e.target)) {
      this.closeOptions();
    }
  };

  onKeyDown = (e) => {
    // Document-level backstop: close on escape, tab, and enter when the key
    // lands OUTSIDE the widget while the list is open. Keys inside the widget
    // are the combobox's own keyboard surface — handleInputKeyDown.
    if (!this.state.isOpen) return;
    if (this.refArea && this.refArea.contains(e.target)) return;
    if (["Escape", "Tab", "Enter"].includes(e.key)) {
      e.preventDefault();
      this.closeOptions();
    }
  };

  listboxId = () => `${this.baseId}-listbox`;

  optionId = (index) => `${this.baseId}-option-${index}`;

  // The keyboard-navigable rows of the open list, in render order: the
  // optional create-new entry first, then the filtered options. (The
  // non-interactive "Loading..." row is deliberately excluded.)
  navigableOptionsOf = (opts) => {
    const items = this.props.newOptionText ? [{ isNewOption: true }] : [];
    return items.concat(this.filterOptions(opts, this.state.inputValue) || []);
  };

  navigableOptions = () => this.navigableOptionsOf(this.props.options);

  setActiveIndex = (activeIndex) => {
    this.setState({ activeIndex }, () => {
      const el = document.getElementById(this.optionId(activeIndex));
      // jsdom has no scrollIntoView — guard so tests exercise the real path.
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "nearest" });
      }
    });
  };

  // Commit the active option. Returns false when nothing is active so Enter
  // can fall back to just closing the list (the pre-combobox behavior).
  selectActiveOption = (e) => {
    const item = this.navigableOptions()[this.state.activeIndex];
    if (!item) return false;
    if (item.isNewOption) {
      this.useInputAndClose(e);
    } else {
      this.handleOptionSelected(item.value);
    }
    return true;
  };

  // WAI-ARIA editable-combobox keyboard surface. DOM focus stays on the text
  // input; the active option is conveyed through aria-activedescendant. Space
  // is deliberately not a select key — this input supports typeahead, so
  // Space must keep inserting spaces into the filter text.
  handleInputKeyDown = (e) => {
    const { isOpen, activeIndex } = this.state;
    const count = this.navigableOptions().length;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!isOpen) {
          this.setState({ isOpen: true });
          if (count) this.setActiveIndex(0);
        } else if (count) {
          this.setActiveIndex(
            activeIndex < 0 ? 0 : Math.min(activeIndex + 1, count - 1)
          );
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (!isOpen) {
          this.setState({ isOpen: true });
          if (count) this.setActiveIndex(count - 1);
        } else if (count) {
          this.setActiveIndex(
            activeIndex < 0 ? count - 1 : Math.max(activeIndex - 1, 0)
          );
        }
        break;
      case "Home":
        if (isOpen && count) {
          e.preventDefault();
          this.setActiveIndex(0);
        }
        break;
      case "End":
        if (isOpen && count) {
          e.preventDefault();
          this.setActiveIndex(count - 1);
        }
        break;
      case "Enter":
        if (isOpen) {
          e.preventDefault();
          if (!this.selectActiveOption(e)) this.closeOptions();
        }
        break;
      case "Escape":
        if (isOpen) {
          e.preventDefault();
          this.closeOptions();
        }
        break;
      case "Tab":
        // Close but let focus move on naturally.
        if (isOpen) this.closeOptions();
        break;
      default:
        break;
    }
  };

  handleFetchOptionsScroll = (e) => {
    const el = e.target;
    if (el.scrollTop + el.clientHeight === el.scrollHeight) {
      const { pagingOptions, fetchOptions } = this.props;
      if (
        pagingOptions &&
        fetchOptions &&
        pagingOptions.currentPage !== pagingOptions.pageCount
      ) {
        fetchOptions({
          page: pagingOptions.currentPage + 1,
          search: this.state.inputValue,
        });
      }
    }
  };

  filterOptions = (opts = [], val = "") => {
    const selectedList = this.props.value || [];
    return !val
      ? opts
      : this.props.isMultiSelect
      ? opts.filter(({ value }) => !selectedList.includes(value))
      : opts.filter(
          ({ label }) =>
            label.toLowerCase().substr(0, val.length) === val.toLowerCase()
        );
  };

  filteredOptions = () =>
    this.filterOptions(this.props.options, this.state.inputValue);

  handleOptionSelected = (value) => {
    const { options, field, isMultiSelect, onChange } = this.props;
    const { label } = options
      ? options.find((opt) => opt.value === value) || { label: field.value }
      : { label: value };
    var newValue = value;
    if (isMultiSelect) {
      // Append into a COPY. Pushing into `field.value` mutated react-hook-form's
      // stored array in place, so the "new" value was reference-identical to the
      // old one — and if the argument happened to BE that same array, the push
      // made it contain itself (see componentDidMount). A fresh array keeps the
      // form's change detection honest and makes a self-reference impossible.
      newValue = [...(Array.isArray(field.value) ? field.value : []), value];
    }
    onChange(newValue);

    this.setState({
      isOpen: false,
      inputValue: !isMultiSelect ? label : "",
      fromOptions: true,
      activeIndex: -1,
    });
  };

  handleInputChange = (v) => {
    const { fetchOptions, alwaysFetchOptions, pagingOptions } = this.props;
    if (
      fetchOptions &&
      (!this.filteredOptions().length || alwaysFetchOptions)
    ) {
      fetchOptions(pagingOptions ? { page: 1, search: v } : v);
    }

    if (this.state.inputValue !== v) {
      this.setState({
        isOpen: true,
        inputValue: v,
        fromOptions: false,
        // A new filter invalidates the previous active row.
        activeIndex: -1,
      });
    }
  };

  // Simple state update functions
  openOptions = (e) => {
    e.stopPropagation();
    if (!this.state.isOpen) {
      this.setState({ isOpen: true });
    }
  };

  closeOptions = () => {
    if (this.state.isOpen) {
      this.setState({ isOpen: false, activeIndex: -1 });
    }
  };

  updateInputValue = (v) => {
    this.setState({ inputValue: v });
  };

  useInputAndClose = (e) => {
    e.stopPropagation();
    const { onChange } = this.props;
    onChange(this.state.inputValue);

    this.setState({
      isOpen: false,
      fromOptions: false,
      activeIndex: -1,
    });
  };

  getValueStr = (path) => {
    var isImagePath =
      path &&
      typeof path === "string" &&
      (path.includes("png") || path.includes("jpg"));
    var result = isImagePath ? path.replace(/\?.*/g, "") : path;
    var pathSplits =
      result && typeof path === "string" ? result.split("/") : [];

    return isImagePath && pathSplits ? pathSplits[pathSplits.length - 1] : path;
  };

  handleOnBlur = (e) => {
    const { onBlur, onChange } = this.props
    onBlur();
    onChange(e);
  }

  render() {
    const {
      label,
      placeholder,
      inputClassName,
      labelClassName,
      validIconName,
      errorIconName,
      emptyIconName,
      newOptionText,
      optionsContainerClass,
      CustomOption,
      leftIcon,
      enableRemoveIcon,
      isFetching,
      isMultiSelect,
      inputDisabled,
      listChanged,
      listSelectChanged,
      // Consumed by ImageSelect, not by Select — pulled out of `...input` so it
      // is never spread onto the underlying DOM input.
      recordDeletedFilterId,
      tooltip,
      enableDropDownIcon,
      permissionRole,
      brandForm,
      onBlur,
      invalid,
      isTouched,
      error,
      ...input
    } = this.props;

    const { isOpen, inputValue, fromOptions, shouldReload, activeIndex } =
      this.state;

    const hasError = isTouched && error,
      isValid = isTouched && invalid;

    const noop = () => (noop) => noop;
    const isExact = (v1, v2) => v1 === v2 && v1.length === v2.length;

    const filteredOptions = this.filteredOptions();
    const liteUserHasFilter =
      input.name === "photo_filters" &&
      permissionRole === "lite" &&
      brandForm["photo_filters"] &&
      brandForm["photo_filters"].length
        ? true
        : false;

    // The list only renders with content behind it; aria-expanded and
    // aria-controls must track the popup's actual presence, not bare isOpen.
    const showList = Boolean(
      isOpen &&
        (newOptionText ||
          isFetching ||
          (filteredOptions && filteredOptions.length > 0))
    );
    const navigableCount =
      (newOptionText ? 1 : 0) + (filteredOptions ? filteredOptions.length : 0);
    const activeOptionId =
      showList && activeIndex >= 0 && activeIndex < navigableCount
        ? this.optionId(activeIndex)
        : undefined;

    return (
      <Wrapper
        onClick={this.openOptions}
        ref={(ref) => (this.refArea = ref)}
      >
        {isMultiSelect && (
          <DraggableList
            data={input.value}
            // Forward the removed item too — this wrapper used to drop every
            // argument past the first, which is what forced deletion to be
            // inferred from the surviving list instead of stated outright.
            listChanged={(list, removedItem) =>
              listChanged && listChanged(list, removedItem)
            }
            listSelectChanged={listSelectChanged} />
        )}
        {!shouldReload && (
          <Input
            {...input}
            value={
              inputValue && inputValue.length
                ? this.getValueStr(inputValue)
                : ""
            }
            id={this.baseId}
            role="combobox"
            aria-expanded={showList}
            aria-haspopup="listbox"
            aria-autocomplete="list"
            aria-controls={showList ? this.listboxId() : undefined}
            aria-activedescendant={activeOptionId}
            onKeyDown={this.handleInputKeyDown}
            onChange={(e) => this.handleInputChange(e.target.value)}
            onBlur={(e) => {
              // Block unwanted change triggers with fancy tricks
              filteredOptions.find(({ label }) => isExact(label, inputValue))
                ? noop
                : this.handleOnBlur(e)
            }}
            hasError={hasError}
            className={`${inputClassName} ${leftIcon ? "has-icon" : ""} ${
              isMultiSelect && input.value ? "multi-select" : ""
            }`}
            placeholder={
              liteUserHasFilter
                ? "lite tier users are limited to one photo filter"
                : placeholder
            }
            disabled={inputDisabled}
            readOnly={this.props.readOnly}
          />
        )}
        {label && (
          <Label
            htmlFor={this.baseId}
            id={`${this.baseId}-label`}
            className={`${labelClassName} ${
              isMultiSelect ? "multi-select" : ""
            }`}
          >
            {label}
          </Label>
        )}
        {leftIcon && <SelectIcon>{leftIcon}</SelectIcon>}
        {enableRemoveIcon && inputValue && (
          <SelectIcon
            className="right pt-1.5"
            role="button"
            tabIndex={0}
            aria-label="Clear selection"
            onClick={() => {
              this.handleInputChange("");
              this.handleOptionSelected("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                this.handleInputChange("");
                this.handleOptionSelected("");
              }
            }}
          >
            <Icon name="x" size="10" />
          </SelectIcon>
        )}
        {enableDropDownIcon && (
          <SelectIcon className="right" aria-hidden="true">
            <Icon name="drop-down" size="12" fill={colors.DARK} />
          </SelectIcon>
        )}
        {showList && (
          <Options
            role="listbox"
            id={this.listboxId()}
            aria-labelledby={label ? `${this.baseId}-label` : undefined}
            aria-multiselectable={isMultiSelect || undefined}
            className={`options-list ${optionsContainerClass || ""}`}
          >
            {newOptionText ? (
              <Option
                role="option"
                id={this.optionId(0)}
                aria-selected={!fromOptions}
                $active={activeIndex === 0}
                onClick={this.useInputAndClose}
                selected={!fromOptions}
              >
                {`${newOptionText} "${this.state.inputValue}"`}
              </Option>
            ) : null}
            {filteredOptions.map(({ value, label }, i) => {
              const index = i + (newOptionText ? 1 : 0);
              const selected = fromOptions && input.value === value;
              const optionProps = {
                key: value,
                role: "option",
                id: this.optionId(index),
                "aria-selected": selected,
                $active: activeIndex === index,
                onClick: () => this.handleOptionSelected(value),
                selected: selected,
                label: label,
                value: value,
              };

              return CustomOption ? (
                <CustomOption {...optionProps} />
              ) : (
                <Option {...optionProps}>{label}</Option>
              );
            })}
            {isFetching ? (
              <Option role="option" aria-disabled="true">
                Loading...
              </Option>
            ) : null}
          </Options>
        )}
        <InputValidator
          hasError={hasError}
          isValid={isValid}
          validIconName={validIconName}
          errorIconName={errorIconName}
          emptyIconName={emptyIconName}
        />
        {tooltip && (
          <Tooltip className="tooltip" width="200px" text={tooltip}>
            <Icon size="12" name="help" />
          </Tooltip>
        )}
      </Wrapper>
    );
  }
}

export default Select;
